import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function HowMuchLifeInsuranceIndiana() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set canonical URL
    const SITE_URL_VAL = 'https://hoosierlifeinsurance.com';
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = `${SITE_URL_VAL}/blog/how-much-life-insurance-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'How Much Life Insurance Do I Need in Indiana? 2025 Calculator Guide',
        description: 'Find out exactly how much life insurance you need in Indiana. Use our step-by-step calculator with Indiana-specific income, mortgage, and education costs to get the right coverage amount.',
        author: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-02-10T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/blog/how-much-life-insurance-indiana`,
        mainEntityOfPage: 'https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana',
        image: { '@type': 'ImageObject', url: 'https://public.readdy.ai/ai/img_res/172e99df1707c00231c597d4b61063a1.jpg', width: 1200, height: 630 },
        timeRequired: 'PT10M',
        wordCount: 2000,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Hoosier Life Insurance',
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        description: 'Independent life insurance agency serving Indianapolis and all of Indiana. Licensed specialists helping families find affordable coverage from 20+ top-rated carriers.',
        areaServed: { '@type': 'State', name: 'Indiana' },
        knowsAbout: ['Life Insurance Indiana', 'Term Life Insurance Indianapolis', 'Coverage Calculator Indiana', 'Indiana Life Insurance Rates']
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much life insurance do I need in Indiana?',
            acceptedAnswer: { '@type': 'Answer', text: 'Most Indiana families need 10–12× their annual income. A family earning $75,000/year should carry $750,000–$900,000 in coverage at minimum. Add your full mortgage balance and $100,000 per child for college costs to get your complete number. The DIME method (Debt + Income + Mortgage + Education) gives the most accurate result.' },
          },
          {
            '@type': 'Question',
            name: 'What is the average life insurance coverage amount in Indiana?',
            acceptedAnswer: { '@type': 'Answer', text: 'The average Hoosier carries around $180,000 in life insurance coverage — far below what most financial advisors recommend. Most Indiana families with a mortgage and children need at least $500,000 to $1.5 million in coverage to protect against income loss, debt, and future education costs.' },
          },
          {
            '@type': 'Question',
            name: 'What does the DIME method stand for for Indiana life insurance?',
            acceptedAnswer: { '@type': 'Answer', text: 'DIME stands for Debt, Income, Mortgage, and Education. Add up all your outstanding debts and final expenses, multiply your annual income by 10–20 years, add your remaining mortgage balance, and add $100,000–$220,000 per child for college. The total is your recommended life insurance coverage amount in Indiana.' },
          },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-how-much-blog-${idx}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // HowTo schema — AI/voice search for "how to calculate life insurance in Indiana"
    const howToSchema = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      '@id': 'https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana#howto',
      name: 'How to Calculate How Much Life Insurance You Need in Indiana',
      description: 'Use the DIME method to calculate the right life insurance coverage amount for Indiana families in 2025. Takes 5–10 minutes.',
      totalTime: 'PT10M',
      estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0', name: 'Free calculation — no obligation' },
      tool: [
        { '@type': 'HowToTool', name: 'Calculator or pen and paper' },
        { '@type': 'HowToTool', name: 'Recent pay stub or tax return' },
        { '@type': 'HowToTool', name: 'Current mortgage statement' },
      ],
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Calculate D — Debt and Final Expenses',
          text: 'Add all outstanding debts: mortgage balance (if not counted separately), car loans, credit cards, student loans, and $12,000 for final expenses/funeral costs. For the average Indiana family this totals $225,000.',
          url: 'https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana#step-d',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Calculate I — Income Replacement',
          text: 'Multiply your annual income by the number of years your family will need support — typically 10 to 20 years. Example: $75,000 × 12 years = $900,000. This ensures your family maintains their standard of living without the primary earner.',
          url: 'https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana#step-i',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Calculate M — Mortgage Balance',
          text: 'Add your full remaining mortgage balance if not already counted in Debt. The average Indiana mortgage is $187,000; Hamilton County mortgages average $320,000+. This ensures your family can stay in the home.',
          url: 'https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana#step-m',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Calculate E — Education Costs',
          text: 'Add $100,000–$120,000 per child for in-state Indiana university tuition (Purdue, IU, IUPUI). Add $200,000–$220,000 per child for private college. Example: 2 children at state schools = $200,000.',
          url: 'https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana#step-e',
        },
        {
          '@type': 'HowToStep',
          position: 5,
          name: 'Add All Four Numbers — Get Your Coverage Target',
          text: 'Add D + I + M + E. For a typical Indiana family earning $75,000 with a $185,000 mortgage and 2 kids: $225,000 + $900,000 + $0 (included in D) + $200,000 = $1,325,000. Get a free quote at hoosierlifeinsurance.com/instant-quote.',
          url: 'https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana#step-total',
        },
      ],
    };

    const howToEl = document.createElement('script');
    howToEl.id = 'schema-how-much-howto';
    howToEl.type = 'application/ld+json';
    howToEl.textContent = JSON.stringify(howToSchema);
    document.head.appendChild(howToEl);

    return () => {
      schemas.forEach((_, idx) => {
        document.getElementById(`schema-how-much-blog-${idx}`)?.remove();
      });
      document.getElementById('schema-how-much-howto')?.remove();
    };
  }, []);

  const coverageSteps = [
    { letter: 'D', label: 'Debt & Final Expenses', desc: 'Mortgage + car loans + credit cards + $12,000 funeral costs', example: '$225,000', color: 'bg-[#1A3A52]' },
    { letter: 'I', label: 'Income Replacement', desc: 'Annual salary × years your family needs support (10–20 yrs)', example: '$900,000', color: 'bg-[#D97642]' },
    { letter: 'M', label: 'Mortgage', desc: 'Remaining balance if not counted above (avoid double-counting)', example: '$0', color: 'bg-[#1A3A52]' },
    { letter: 'E', label: 'Education', desc: 'Purdue/IU: ~$100K per child | Private: ~$220K per child', example: '$200,000', color: 'bg-[#D97642]' },
  ];

  const rateTable = [
    { age: '25', gender: 'Male', health: 'Preferred', coverage: '$500K / 20yr', monthly: '$18' },
    { age: '30', gender: 'Female', health: 'Preferred', coverage: '$500K / 20yr', monthly: '$16' },
    { age: '35', gender: 'Male', health: 'Standard', coverage: '$750K / 20yr', monthly: '$42' },
    { age: '40', gender: 'Female', health: 'Standard', coverage: '$750K / 20yr', monthly: '$38' },
    { age: '45', gender: 'Male', health: 'Preferred', coverage: '$1M / 20yr', monthly: '$78' },
    { age: '50', gender: 'Female', health: 'Standard', coverage: '$500K / 20yr', monthly: '$92' },
  ];

  return (
    <>
      <title>How Much Life Insurance Do I Need in Indiana? 2026 | Hoosier Life</title>
      <meta name="description" content="Find out exactly how much life insurance you need in Indiana. Step-by-step DIME calculator with 2026 Indiana mortgage, income, and college cost data. Takes 2 minutes." />
      <meta name="keywords" content="how much life insurance Indiana, life insurance calculator Indiana, coverage amount Indiana families, Indianapolis life insurance needs" />
      <meta property="og:title" content="How Much Life Insurance Do I Need in Indiana? | 2025 Calculator" />
      <meta property="og:description" content="Step-by-step guide to calculating the right life insurance coverage for Indiana families. Real data, real numbers." />
      <meta property="og:url" content={`https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana`} />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/blog/how-much-life-insurance-indiana" />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:image" content="https://readdy.ai/api/search-image?query=Indiana%20family%20at%20kitchen%20table%20reviewing%20life%20insurance%20calculator%20worksheet%20warm%20home%20lighting%20natural%20daylight%20clean%20white%20background%20professional%20couple&width=1200&height=630&seq=blog018og&orientation=landscape" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=Indiana%20family%20at%20kitchen%20table%20reviewing%20life%20insurance%20calculator%20worksheet%20warm%20home%20lighting%20natural%20daylight%20clean%20white%20background%20professional%20couple&width=1200&height=630&seq=blog018og&orientation=landscape" />

      <BlogProgressBar />
      <Header />

      <article className="min-h-screen bg-white">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-[#D97642] mb-6 transition-colors text-sm">
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#D97642]/20 text-[#D97642] text-xs font-bold px-3 py-1.5 rounded-full">Term Life</span>
              <span className="text-white/50 text-xs">10 min read · February 10, 2025</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">How Much Life Insurance Do Indiana Residents Actually Need? 2026 Guide</h1>
            <p className="text-white/75 text-xl leading-relaxed max-w-2xl">The honest, numbers-first answer for Hoosier families — with a step-by-step calculator and real 2025 Indiana data.</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">

          {/* Key Takeaways — AI extraction box */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Most Indiana families need 10–12× their annual income — a $75K earner needs $750K–$900K minimum',
                'The average Hoosier carries just $180,000 in coverage — a dangerous gap that leaves families financially exposed',
                'Use the DIME method: Debt + Income replacement + Mortgage + Education costs for your exact number',
                'A family earning $75K/year with 2 kids and a mortgage typically needs $1.3M in total coverage',
                'A healthy 35-year-old Indiana male pays ~$42/month for $750K of 20-year term — coverage is more affordable than most families expect',
                'Waiting 10 years to buy costs thousands more: a 35-year-old pays $42/month vs. $88/month at age 45 for the same coverage',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Quick answer */}
          <div className="bg-[#1A3A52]/5 border-l-4 border-[#1A3A52] p-6 rounded-r-xl mb-10">
            <p className="font-bold text-gray-900 mb-1 flex items-center gap-2"><i className="ri-lightbulb-flash-line text-[#D97642]"></i>Quick Answer</p>
            <p className="text-gray-700 leading-relaxed">
              Most Indiana families need <strong>10–12× their annual income</strong>. Earning $75,000/year? You need $750,000–$900,000 minimum. Add your mortgage balance and $100,000 per child for college to get the full picture. The average Hoosier carries just $180,000 — a dangerous gap. For the most <Link to="/blog/cheapest-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">affordable way to get the right coverage</Link>, term life is the answer for most families.
            </p>
          </div>

          {/* Intro */}
          <p className="text-gray-700 text-lg leading-relaxed mb-8">
            "How much life insurance do I need?" is the most important question in personal finance — and the most often answered wrong. Most people guess, pick a round number, or just accept whatever their employer offers (typically 1–2× salary, which is barely enough to cover final expenses). This guide gives you a real, Indiana-specific answer in under 10 minutes.
          </p>

          {/* DIME Method */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">The DIME Method: Your Indiana Coverage Formula</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Add four numbers — your total Debt (including final expenses), Income replacement (annual salary × 10–20 years), Mortgage balance, and Education costs per child. For most Indiana families, this totals $750,000 to $1.5 million.
            </div>
            <p className="text-gray-600 mb-7">Four numbers. Add them up. That's your number. Here's how it works for the average Hoosier family:</p>

            <div className="space-y-4 mb-8">
              {coverageSteps.map(s => (
                <div key={s.letter} className="flex items-start gap-5 bg-white border border-gray-100 rounded-xl p-5">
                  <div className={`w-12 h-12 ${s.color} text-white rounded-xl flex items-center justify-center flex-shrink-0 text-xl font-bold`}>{s.letter}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-gray-900">{s.label}</span>
                      <span className="font-bold text-[#D97642] text-lg">{s.example}</span>
                    </div>
                    <p className="text-gray-600 text-sm">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-[#D97642] text-white rounded-xl p-6">
              <div className="flex items-center justify-between text-xl font-bold">
                <span>Total Recommended Coverage</span>
                <span>$1,325,000</span>
              </div>
              <p className="text-white/75 text-sm mt-2">Example: Indiana family, $75K income, 2 kids, $185K mortgage. This is about 17.7× annual income — higher than the 10× rule because of college costs and debt payoff.</p>
            </div>
          </section>

          {/* Life stage table */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Coverage by Life Stage — Indiana 2025</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A single Hoosier in their 20s needs $250K–$500K. A young family with kids needs $1M–$1.5M. An empty nester in their 50s can reduce to $250K–$500K as debts and dependents decrease.
            </div>
            <p className="text-gray-600 mb-6">Your needs change as your life changes. Here's a quick-reference guide:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-5 py-3 text-left font-semibold">Life Stage</th>
                    <th className="px-5 py-3 text-left font-semibold">Income</th>
                    <th className="px-5 py-3 text-left font-semibold">Recommended</th>
                    <th className="px-5 py-3 text-left font-semibold">Monthly Cost*</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { stage: 'Single, 20s', income: '$40K', rec: '$250K–$500K', cost: '$12–$20' },
                    { stage: 'Newlywed, no kids', income: '$65K', rec: '$500K–$750K', cost: '$25–$40' },
                    { stage: 'Young family, 1–2 kids', income: '$75K', rec: '$1M–$1.5M', cost: '$50–$75' },
                    { stage: 'Established family, teens', income: '$95K', rec: '$1M–$2M', cost: '$80–$130' },
                    { stage: 'Empty nester, 50s', income: '$85K', rec: '$250K–$500K', cost: '$100–$180' },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium text-gray-900">{row.stage}</td>
                      <td className="px-5 py-3 text-gray-600">{row.income}</td>
                      <td className="px-5 py-3 text-gray-800 font-semibold">{row.rec}</td>
                      <td className="px-5 py-3 text-[#D97642] font-bold">{row.cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">*20-year term life, healthy non-smoker, Indianapolis area rates</p>
          </section>

          {/* Sample rates */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Real Indiana Rates: What You'll Pay in 2025</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A healthy 25-year-old Indiana male pays $18/month for $500K of 20-year term. A 45-year-old male pays $78/month for $1M of 20-year term. Women pay 10–15% less than men at all ages. Prefer no medical exam? See our <Link to="/blog/no-medical-exam-life-insurance-indiana" className="text-teal-600 hover:text-teal-700 font-semibold underline">Indiana no-exam life insurance guide</Link>.
            </div>
            <p className="text-gray-600 mb-6">Actual rates from top carriers serving Central Indiana. These are fully underwritten prices — no medical exam shortcuts:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Age', 'Gender', 'Health Class', 'Policy', 'Monthly Premium'].map(h => (
                      <th key={h} className="px-5 py-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rateTable.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium">{r.age}</td>
                      <td className="px-5 py-3 text-gray-600">{r.gender}</td>
                      <td className="px-5 py-3 text-gray-600">{r.health}</td>
                      <td className="px-5 py-3 text-gray-800">{r.coverage}</td>
                      <td className="px-5 py-3 text-[#D97642] font-bold">{r.monthly}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Related Blog Links */}
          <div className="bg-gray-50 rounded-2xl p-7 mb-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-links-line text-[#D97642]"></i>
              Related Reading
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/mortgage-protection-indianapolis" className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-200 hover:border-[#D97642]/40 transition-colors group">
                <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-4-line text-[#1A3A52]"></i>
                </div>
                <div>
                  <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors block">Mortgage Protection Insurance</span>
                  <p className="text-xs text-gray-500 mt-1">How to factor your mortgage into your total coverage needs</p>
                </div>
              </Link>
              <Link to="/blog/term-life-insurance-indianapolis" className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-200 hover:border-[#D97642]/40 transition-colors group">
                <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-[#1A3A52]"></i>
                </div>
                <div>
                  <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors block">Term Life Insurance Guide</span>
                  <p className="text-xs text-gray-500 mt-1">The most affordable way to get the coverage you need</p>
                </div>
              </Link>
            </div>
          </div>

          {/* 3 mistakes */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">3 Coverage Mistakes Indiana Families Make Most</h2>
            <div className="space-y-4">
              {[
                { icon: 'ri-close-circle-line', color: 'text-red-500', title: 'Trusting employer coverage alone', body: "Your job's group life policy is 1–2× salary and disappears the day you leave. It's a starting point, not a plan. See how this compares to a proper individual policy in our guide to the best life insurance carriers in Indianapolis." },
                { icon: 'ri-close-circle-line', color: 'text-red-500', title: "Not covering the stay-at-home parent", body: "Childcare in Indiana runs $12,000–$18,000 per child per year. A $350,000 policy on a stay-at-home parent covers those costs for 15 years. Our guide to life insurance for Indianapolis families covers this scenario in detail." },
                { icon: 'ri-close-circle-line', color: 'text-red-500', title: 'Waiting until you need it', body: "A 35-year-old pays $42/month for $750K of coverage. A 45-year-old pays $88/month for the same policy. Ten years of delay costs you thousands. Check today's rates in our Indianapolis life insurance rates guide." },
              ].map((m, i) => (
                <div key={i} className="bg-red-50 border-l-4 border-red-400 p-5 rounded-r-xl">
                  <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <i className={`${m.icon} ${m.color}`}></i>
                    {m.title}
                  </h3>
                  <p className="text-gray-700 text-sm">{m.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Serving Your City */}
          <div className="bg-[#F9F7F4] rounded-2xl p-7 my-10">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Coverage Calculators by Indiana City</h2>
            <p className="text-sm text-gray-600 mb-5">Incomes, home values, and school costs differ across Indiana. See city-specific guidance for your area:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/life-insurance-indianapolis', city: 'Indianapolis', desc: 'Marion County families — median income & mortgage data' },
                { to: '/life-insurance-fort-wayne', city: 'Fort Wayne', desc: 'Allen County — manufacturing & healthcare workers' },
                { to: '/life-insurance-south-bend', city: 'South Bend', desc: 'Notre Dame corridor & AM General employees' },
                { to: '/life-insurance-evansville', city: 'Evansville', desc: 'Toyota & ALCOA workers approaching retirement' },
                { to: '/hamilton-county-life-insurance', city: 'Hamilton County', desc: 'Highest median income county — larger coverage needs' },
              ].map(({ to, city, desc }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-100 hover:border-[#D97642]/40 transition-colors group cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                    <i className="ri-calculator-line text-[#D97642] text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors">{city}</span>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{desc}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#D97642] transition-colors flex-shrink-0"></i>
                </Link>
              ))}
            </div>
          </div>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 mb-10 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                Our team of licensed Indiana life insurance agents has helped thousands of Hoosier families calculate the right coverage amount and find affordable policies. We are independent agents — not tied to any single carrier — so we shop 20+ A-rated companies on your behalf. All rate data and coverage recommendations are based on current Indiana market conditions and are reviewed regularly.
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center text-white mb-12">
            <h2 className="text-2xl font-bold mb-3">Know Your Number. Get Your Quote.</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">We'll run your DIME calculation, shop 10+ carriers, and have a quote in your inbox in under 10 minutes.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap">Get Instant Quote</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap">Book Free Review</Link>
            </div>
          </section>

        </div>
      </article>

      <BlogRelatedStrip currentSlug="how-much-life-insurance-indiana" />
      <Footer />
    </>
  );
}
